import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import SearchUsersModal from './SearchUsersModal';
import logo from '../Pages/HomePage/Images/LTM.png';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Check authentication status on component mount and when localStorage changes
  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem('token'); // Changed from 'authToken' to 'token'
      const userData = localStorage.getItem('user');
      
      console.log('Header checking auth status:', { token: !!token, userData: !!userData });
      
      if (token && userData) {
        setIsLoggedIn(true);
        setUser(JSON.parse(userData));
        console.log('Header: User logged in:', JSON.parse(userData));
      } else {
        setIsLoggedIn(false);
        setUser(null);
        console.log('Header: User not logged in');
      }
    };

    checkAuthStatus();

    // Listen for storage changes (for when user logs in/out in another tab)
    window.addEventListener('storage', checkAuthStatus);
    
    // Also check on focus (when user comes back to tab)
    window.addEventListener('focus', checkAuthStatus);
    
    return () => {
      window.removeEventListener('storage', checkAuthStatus);
      window.removeEventListener('focus', checkAuthStatus);
    };
  }, []);

  // Additional effect to check auth status periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData && !isLoggedIn) {
        console.log('Header: Detected login, updating state');
        setIsLoggedIn(true);
        setUser(JSON.parse(userData));
      } else if (!token && isLoggedIn) {
        console.log('Header: Detected logout, updating state');
        setIsLoggedIn(false);
        setUser(null);
      }
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, [isLoggedIn]);

  // Auto-hide header on scroll for mobile
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isMobile = window.innerWidth <= 768;
      
      if (!isMobile) {
        setIsHeaderVisible(true);
        return;
      }

      // Show header ONLY when at the very top of page
      if (currentScrollY <= 10) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Hide header when scrolling down and past 100px
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Show header when scrolling up
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleLogout = () => {
    localStorage.removeItem('token'); // Changed from 'authToken' to 'token'
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUser(null);
    navigate('/');
  };

  const handleExploreTherapy = () => {
    if (!isLoggedIn) {
      // Redirect to login page if not logged in
      navigate('/login');
    } else {
      // Navigate to explore page if logged in
      navigate('/explore');
    }
  };


  const handleTooltipEnter = (tooltipId) => {
    setActiveTooltip(tooltipId);
  };

  const handleTooltipLeave = () => {
    setActiveTooltip(null);
  };

  // Function to check if a route is active
  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  // Handle mobile menu toggle
  const handleMobileMenuToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    console.log('Mobile menu toggled! Current state:', isMobileMenuOpen);
    setIsMobileMenuOpen(!isMobileMenuOpen);
    console.log('New state will be:', !isMobileMenuOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.header')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <div className={`header ${isHeaderVisible ? 'header-visible' : 'header-hidden'}`}>
        <div className="header-content">
          <div className="header-left">
            <div className="logo-container">
              <Link 
                to="/" 
                className={`logo ${activeTooltip === 'logo' ? 'active' : ''}`}
                onMouseEnter={() => handleTooltipEnter('logo')}
                onMouseLeave={handleTooltipLeave}
              >
                <img src={logo} alt="Learning to Mend Logo" className="logo-image" />
                Learning to Mend
              </Link>
            </div>
            {/* Mobile indicator - positioned separately */}
            <div 
              className={`mobile-indicator ${isMobileMenuOpen ? 'active' : ''}`}
              onClick={handleMobileMenuToggle}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleMobileMenuToggle(e);
                }
              }}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? '✕' : '☰'}
            </div>
          </div>

          <div className="nav-links">
              <div className="nav-item-container">
                <Link 
                  to="/music" 
                  className={`nav-link ${isActiveRoute('/music') ? 'active' : ''}`}
                  onMouseEnter={() => handleTooltipEnter('music')}
                  onMouseLeave={handleTooltipLeave}
                >
                  Thư Giãn
                </Link>
              </div>
              
              <div className="nav-item-container">
                <Link 
                  to="/volunteering" 
                  className={`nav-link ${isActiveRoute('/volunteering') ? 'active' : ''}`}
                  onMouseEnter={() => handleTooltipEnter('volunteering')}
                  onMouseLeave={handleTooltipLeave}
                >
                  Tình nguyện
                </Link>
              </div>
              
              <div className="nav-item-container">
                <Link 
                  to="/book-expert" 
                  className={`nav-link ${isActiveRoute('/book-expert') ? 'active' : ''}`}
                  onMouseEnter={() => handleTooltipEnter('organizations')}
                  onMouseLeave={handleTooltipLeave}
                >
                  Tổ chức
                </Link>
              </div>
              
              <div className="nav-item-container">
                <Link 
                  to="/free-chat" 
                  className={`nav-link ${isActiveRoute('/free-chat') ? 'active' : ''}`}
                  onMouseEnter={() => handleTooltipEnter('free-chat')}
                  onMouseLeave={handleTooltipLeave}
                >
                  Trò chuyện
                </Link>
              </div>
              
              <div className="nav-item-container">
                <Link 
                  to="/appointment-calendar" 
                  className={`nav-link ${isActiveRoute('/appointment-calendar') ? 'active' : ''}`}
                  onMouseEnter={() => handleTooltipEnter('dashboard')}
                  onMouseLeave={handleTooltipLeave}
                >
                  Tài khoản
                </Link>
              </div>
              
          </div>

          <div className="header-right">
            {isLoggedIn ? (
              <>
                <Link to="/appointment-calendar?section=user-details" className="user-greeting-link">
                  Xin chào, {user?.username || user?.name || 'Người dùng'}!
                </Link>
                <button onClick={handleLogout} className="logout-btn">Đăng xuất</button>
              </>
            ) : (
              <>
                <Link to="/login" className="login-link">Đăng nhập</Link>
                <button onClick={handleExploreTherapy} className="explore-therapy-btn">Khám phá</button>
              </>
            )}
          </div>
        </div>
  <SearchUsersModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
        
        {/* Mobile Dropdown Menu */}
        <div className={`mobile-nav ${isMobileMenuOpen ? 'active' : ''}`} style={{display: isMobileMenuOpen ? 'block' : 'none'}}>
          <div className="mobile-nav-links">
            <div className="mobile-nav-section-header">
              <h3>Điều hướng</h3>
            </div>
            <Link 
              to="/music" 
              className={`mobile-nav-link ${isActiveRoute('/music') ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Chữa lành
            </Link>
            <Link 
              to="/volunteering" 
              className={`mobile-nav-link ${isActiveRoute('/volunteering') ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Tình nguyện
            </Link>
            <Link 
              to="/book-expert" 
              className={`mobile-nav-link ${isActiveRoute('/book-expert') ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Tổ chức
            </Link>
            <Link 
              to="/free-chat" 
              className={`mobile-nav-link ${isActiveRoute('/free-chat') ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Trò chuyện
            </Link>
          </div>
          <div className="mobile-nav-actions">
            <div className="mobile-nav-section-header">
              <h3>{isLoggedIn ? 'Tài khoản' : 'Bắt đầu'}</h3>
            </div>
            {isLoggedIn ? (
              <>
                <div className="mobile-user-info">
                  <span className="mobile-user-greeting">Xin chào, {user?.username || user?.name || 'Người dùng'}!</span>
                  <Link 
                    to="/appointment-calendar?section=user-details" 
                    className="mobile-profile-link" 
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Xem hồ sơ
                  </Link>
                </div>
                <button 
                  className="mobile-logout-btn" 
                  onClick={() => { 
                    handleLogout(); 
                    setIsMobileMenuOpen(false); 
                  }}
                >
                  Đăng xuất
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="mobile-nav-link" 
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Đăng nhập
                </Link>
                <button 
                  className="mobile-therapy-btn" 
                  onClick={() => {
                    handleExploreTherapy();
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Khám phá
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;